import React, { useState, useEffect } from 'react';
import PaymentService from './PaymentService';
import Alert from 'react-bootstrap/Alert';
import filterFieldsForList from '../../common/functions/filterFieldsForList';
import schema from './productJsonSchema';
import useHeaderGenerator from 'components/app/common/functions/useHeaderGenerator';
import useTableGenerator from '../../common/functions/useTableGenerator';
import { useNavigate } from 'react-router-dom';
import { Card, Modal, Form, Spinner, Badge } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const WODProductTable = () => {
  const [columns, setColumns] = useState([]);
  const [error, setError] = useState('');
  const [noDataToShow, setNoDataToShow] = useState(true);
  const [tableGenerated, setTableGenerated] = useState();
  const [refreshList, setRefreshList] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [productsOnStripe, setProductsOnStripe] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [productsByAccount, setProductsByAccount] = useState([]);
  const [showModalEditProduct, setShowModalEditProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [moreInformation, setMoreInformation] = useState('');
  const [maxClasses, setMaxClasses] = useState('');
  const [isSavingProduct, setIsSavingProduct] = useState(false);
  const navigate = useNavigate();

  const handleAddProduct = () => {
    navigate('/app/wod/settings/products/create-form');
  };

  const deleteWODProduct = identification => {
    PaymentService.deleteWODProduct(identification)
      .then(response => {
        console.log(response);
      })
      .catch(error => console.log(error));
  };

  const jsonSchema = schema;

  const handleToggleProduct = productIndex => {
    setSelectedProducts(prev => ({
      ...prev,
      [productIndex]: !prev[productIndex]
    }));
  };

  const handleSelectAll = () => {
    const newSelection = {};
    productsOnStripe.forEach((_, index) => {
      newSelection[index] = true;
    });
    setSelectedProducts(newSelection);
  };

  const handleUnselectAll = () => {
    const newSelection = {};
    productsOnStripe.forEach((_, index) => {
      newSelection[index] = false;
    });
    setSelectedProducts(newSelection);
  };

  const getSelectedCount = () => {
    return Object.values(selectedProducts).filter(Boolean).length;
  };

  const handleImportFromStripe = () => {
    setIsLoading(true);
    PaymentService.retrieveAllProductsFromStripe()
      .then(response => {
        console.log('Products imported from Stripe:', response);
        const updatedProducts = response.filter(
          product =>
            !productsByAccount.some(
              p => p.stripeProductId === product.productId
            )
        );
        setProductsOnStripe(updatedProducts);
        setSelectedProducts({});
        setModalShow(true);
        setIsLoading(false);
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const handleSaveSelectedProducts = async () => {
    const productsToImport = productsOnStripe.filter(
      (_, index) => selectedProducts[index]
    );

    if (productsToImport.length === 0) {
      alert('Please select at least one product to import.');
      return;
    }

    setIsImporting(true);

    try {
      const importPromises = productsToImport.map(product =>
        PaymentService.importProductFromStripe(product)
      );
      await Promise.all(importPromises);
      console.log('All products imported successfully');
      setProductsOnStripe([]);
      setSelectedProducts({});
      setModalShow(false);
      setRefreshList(true);
    } catch (error) {
      console.error('Error importing products:', error);
      alert('Error importing products. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };
  const handleEditProduct = identifier => {
    const product = productsByAccount.find(p => p.identifier === identifier);
    console.log('Product', product, identifier);
    if (product) {
      setEditingProduct(product);
      setMoreInformation(product.moreInformation || '');
      setMaxClasses(
        product.maxClasses !== undefined && product.maxClasses !== null
          ? product.maxClasses
          : ''
      );
      setShowModalEditProduct(true);
    }
  };

  const handleSaveEditProduct = async () => {
    if (!editingProduct) {
      return;
    }
    setIsSavingProduct(true);
    try {
      const productData = {
        ...editingProduct,
        moreInformation,
        maxClasses,
        billingCyrcle: { value: editingProduct.billingCyrcle }
      };
      await PaymentService.saveWODProduct(productData);
      setShowModalEditProduct(false);
      setEditingProduct(null);
      setRefreshList(true);
    } catch (error) {
      console.log('Error updating product', error);
    } finally {
      setIsSavingProduct(false);
    }
  };

  useEffect(() => {
    const fieldsForList = filterFieldsForList(jsonSchema);
    setColumns(fieldsForList);

    PaymentService.retrieveAllProductsByAccount()
      .then(data => {
        const updatedData = data;
        setProductsByAccount(updatedData);
        const actionButtons = [
          {
            icon: 'edit',
            onClick: identifier => {
              handleEditProduct(identifier);
              setRefreshList(true);
            },
            className: 'p-0',
            title: 'Edit',
            variant: 'action'
          },
          {
            icon: 'trash-alt',
            onClick: identifier => {
              deleteWODProduct(identifier);
              setRefreshList(true);
            },
            className: 'p-0',
            title: 'Delete',
            variant: 'action'
          }
        ];

        setNoDataToShow(updatedData.length === 0);
        setTableGenerated(
          useTableGenerator(updatedData, jsonSchema, actionButtons)
        );
        setRefreshList(false);
      })
      .catch(error => setError(error.message));
  }, [refreshList]);

  const buttonsConf = [];
  const headerGenerated = useHeaderGenerator('WODProduct', ' ', buttonsConf);

  return (
    <>
      <Modal
        show={modalShow}
        onHide={() => setModalShow(false)}
        size="lg"
        centered
      >
        {isLoading ? (
          <>
            <Modal.Header closeButton>
              <Modal.Title>Loading Products...</Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </Modal.Body>
          </>
        ) : (
          <>
            <Modal.Header closeButton>
              <Modal.Title>
                <FontAwesomeIcon icon="stripe" className="me-2" />
                Import Products from Stripe
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {productsOnStripe && productsOnStripe.length > 0 ? (
                <>
                  <div className="mb-3">
                    <p className="text-700">
                      Review and select the products you want to import.
                      <Badge bg="secondary" className="ms-2">
                        {getSelectedCount()} of {productsOnStripe.length}{' '}
                        selected
                      </Badge>
                    </p>
                    <div className="d-flex gap-2 mb-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleSelectAll}
                        disabled={isImporting}
                      >
                        <FontAwesomeIcon icon="check-square" className="me-1" />
                        Select All
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleUnselectAll}
                        disabled={isImporting}
                      >
                        <FontAwesomeIcon icon="square" className="me-1" />
                        Unselect All
                      </Button>
                    </div>
                  </div>
                  {productsOnStripe.map((product, index) => (
                    <div
                      key={product.id || index}
                      className={`border rounded p-3 mb-2 ${
                        selectedProducts[index] ? 'bg-light' : ''
                      }`}
                    >
                      <Form.Check
                        type="checkbox"
                        id={`product-${index}`}
                        checked={selectedProducts[index] || false}
                        onChange={() => handleToggleProduct(index)}
                        disabled={isImporting}
                        label={
                          <div className="d-flex align-items-center justify-content-between w-100">
                            <div>
                              <strong>{product.name}</strong>
                              <div className="text-muted small">
                                ID: {product.productId}
                              </div>
                            </div>
                            <Badge bg="success">£{product.price}</Badge>
                          </div>
                        }
                      />
                    </div>
                  ))}
                </>
              ) : (
                <p className="text-center text-muted">Nothing to import</p>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setModalShow(false)}
                disabled={isImporting}
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveSelectedProducts}
                disabled={isImporting || getSelectedCount() === 0}
              >
                {isImporting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Importing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon="download" className="me-2" />
                    Import {getSelectedCount()} Product
                    {getSelectedCount() !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
      <Modal
        show={showModalEditProduct}
        onHide={() => setShowModalEditProduct(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingProduct && (
            <>
              {editingProduct.stripeProductId && (
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semi-bold">
                    Stripe Product Id
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={editingProduct.stripeProductId}
                    disabled
                    readOnly
                  />
                </Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semi-bold">Description</Form.Label>
                <Form.Control
                  type="text"
                  value={editingProduct.description || ''}
                  disabled
                  readOnly
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semi-bold">Billing cyrcle</Form.Label>
                <Form.Control
                  type="text"
                  value={editingProduct.billingCyrcle || ''}
                  disabled
                  readOnly
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semi-bold">Price</Form.Label>
                <Form.Control
                  type="text"
                  value={editingProduct.price || ''}
                  disabled
                  readOnly
                />
              </Form.Group>
              <hr />
              <Form.Group className="mb-3">
                <Form.Label className="fw-semi-bold">Max Classes</Form.Label>
                <Form.Control
                  type="number"
                  value={maxClasses}
                  onChange={e => setMaxClasses(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semi-bold">
                  More information
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={moreInformation}
                  onChange={e => setMoreInformation(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semi-bold">Hidden for athletes</Form.Label>
                <Form.Check
                  type="checkbox"
                  checked={editingProduct.hidden || false}
                  onChange={e =>
                    setEditingProduct(prev => ({
                      ...prev,
                      hidden: e.target.checked
                    }))
                  }
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModalEditProduct(false)}
            disabled={isSavingProduct}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEditProduct}
            disabled={isSavingProduct}
          >
            {isSavingProduct ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      {error}

      {noDataToShow && (
        <Alert key="warning" variant="warning">
          There's not any products to show.
        </Alert>
      )}
      <Card>
        <Card.Header>
          <h5 className="text-primary">Products</h5>
        </Card.Header>
        <Card.Body> {tableGenerated}</Card.Body>
        <Card.Footer>
          <Button icon="plus" onClick={handleAddProduct} className="btn btn-sm">
            Add product
          </Button>
          <Button
            onClick={() => handleImportFromStripe()}
            className="btn btn-sm m-1"
            variant="secondary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
                Loading...
              </>
            ) : (
              'Import From Stripe'
            )}
          </Button>
        </Card.Footer>
      </Card>
    </>
  );
};

export default WODProductTable;
